import React, { useState, useEffect, useCallback } from 'react';
import Badge from './Badge';
import PointsService, { type Badge as BadgeType, type MemberBadge } from '../../services/pointsService';

interface BadgeGalleryProps {
  memberId: string;
  isOpen: boolean;
  onClose: () => void;
}

const BadgeGallery: React.FC<BadgeGalleryProps> = ({ memberId, isOpen, onClose }) => {
  const [allBadges, setAllBadges] = useState<BadgeType[]>([]);
  const [memberBadges, setMemberBadges] = useState<MemberBadge[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const pointsService = PointsService.getInstance();

  const loadBadges = useCallback(async () => {
    setLoading(true);
    try {
      const [badges, userBadges] = await Promise.all([
        pointsService.getAllBadges(),
        pointsService.getMemberBadges(memberId)
      ]);

      setAllBadges(badges);
      setMemberBadges(userBadges);
    } catch (error) {
      console.error('Erro ao carregar badges:', error);
    } finally {
      setLoading(false);
    }
  }, [memberId, pointsService]);

  useEffect(() => {
    if (isOpen) {
      void loadBadges();
    }
  }, [isOpen, loadBadges]);

  const isBadgeUnlocked = (badgeId: string): boolean => {
    return memberBadges.some(mb => mb.badgeId === badgeId);
  };

  const filteredBadges = selectedCategory === 'all'
    ? allBadges
    : allBadges.filter(b => b.category === selectedCategory);

  const unlockedCount = memberBadges.length;
  const totalCount = allBadges.length;
  const progressPercentage = totalCount > 0 ? Math.round((unlockedCount / totalCount) * 100) : 0;

  const categories = [
    { id: 'all', name: 'Todos', icon: '🏆' },
    { id: 'streak', name: 'Streaks', icon: '🔥' },
    { id: 'quiz', name: 'Quiz', icon: '🧠' },
    { id: 'level', name: 'Níveis', icon: '⭐' },
    { id: 'special', name: 'Especiais', icon: '💎' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-3xl font-bold flex items-center gap-2">
                🏆 Galeria de Badges
              </h2>
              <p className="text-blue-100 mt-1">
                {unlockedCount} de {totalCount} badges conquistados ({progressPercentage}%)
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          {/* Barra de Progresso Geral */}
          <div className="mt-4 bg-white bg-opacity-20 rounded-full h-3 overflow-hidden">
            <div
              className="bg-white h-full transition-all duration-500"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        {/* Filtros de Categoria */}
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex gap-2 overflow-x-auto">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`
                  px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap
                  ${selectedCategory === cat.id
                    ? 'bg-purple-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }
                `}
              >
                <span className="mr-2">{cat.icon}</span>
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Grid de Badges */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : filteredBadges.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">Nenhum badge encontrado nesta categoria</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {filteredBadges.map(badge => (
                <Badge
                  key={badge.id}
                  badge={badge}
                  unlocked={isBadgeUnlocked(badge.id)}
                  size="md"
                  showProgress={!isBadgeUnlocked(badge.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer com Estatísticas */}
        <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">{unlockedCount}</p>
              <p className="text-sm text-gray-600">Desbloqueados</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-400">{totalCount - unlockedCount}</p>
              <p className="text-sm text-gray-600">Bloqueados</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-600">
                {memberBadges.filter(mb => mb.badge?.tier === 'gold').length}
              </p>
              <p className="text-sm text-gray-600">Ouro</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-cyan-600">
                {memberBadges.filter(mb => mb.badge?.tier === 'diamond').length}
              </p>
              <p className="text-sm text-gray-600">Diamante</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BadgeGallery;
